<template>
  <div class="az-price-calendar">
    <div flex="~ col" h="full">
      <div text="lg left" p="y-3 l-12" font="600">
        {{ t('key.price.calendar.select.options') }}
      </div>
      <h2 v-if="state.params.skuName" text="base left" px="4" font="500">
        {{ state.params.skuName }}
      </h2>
      <!-- 日期选择 -->
      <div p="l-4 y-5" flex="1" overflow="y-auto">
        <div text="base black" font="600" m="b-1.5">
          {{ t('key.price.calendar.date') }}
        </div>
        <div flex="~ items-center justify-between nowrap" m="b-3" class="scrollbar-none space-x-2">
          <div flex="~ 1 items-center" p="t-2" class="overflow-x-scroll space-x-2">
            <div v-for="item in defaultCalendarDate" :key="item.date" :data-date="item.date" class="relative" flex="shrink-0" @click="onSelectDate(item)">
              <div v-if="dayjs(item.date).isToday() || dayjs(item.date).isTomorrow()" class="absolute top--2.5 z-1">
                <van-tag color="var(--el-color-primary-light-9)" text-color="var(--tc-color-primary)">
                  <span v-if="dayjs(item.date).isToday()" text="2.5">{{ t('key.attraction.list.today') }}</span>
                  <span v-if="dayjs(item.date).isTomorrow()" text="2.5">{{ t('key.attraction.list.tomorrow') }}</span>
                </van-tag>
              </div>
              <div
                b="1.5 solid #D9D9D9" p="3" rounded text="xs black"
                :style="[
                  state.selected.date === item.date ? { borderColor: 'var(--tc-color-primary)', color: 'var(--tc-color-primary)', backgroundColor: 'var(--el-color-primary-light-9)' } : {},
                  isDisabledDate(item) ? { borderColor: '#D9D9D980', color: '#D9D9D9' } : {},
                ]"
              >
                {{ dayjs(item.date).format('ddd, MMM D') }}
              </div>
            </div>

            <div v-if="!defaultCalendarDate?.some(i => i.date === state.selected.date)" class="relative" flex="shrink-0" :data-date="state.selected.date">
              <div
                b="1.5 solid" p="3" rounded text="xs" bg="#B5FFE94F"
                :style="{ borderColor: 'var(--tc-color-primary)', color: 'var(--tc-color-primary)' }"
              >
                {{ dayjs(state.selected.date).format('ddd, MMM D') }}
              </div>
            </div>
          </div>
          <div bg="white" p="l-2 r-3 t-2" @click="showCalendar = true">
            <NuxtIcon name="my-icon:calendar" class="align-middle" text="3xl #333333" :style="{ color: 'var(--tc-color-primary)' }" />
          </div>
        </div>

        <div flex="~ items-center wrap" text="2.5 #999999" leading="4">
          <van-icon name="info-o" class="mr-1" /><span flex="shrink-0">{{ t('key.price.calendar.local.time') }}</span>   <el-divider direction="vertical" />  {{ userStore.userInfo.currency }}  <el-divider direction="vertical" />   <span flex="shrink-0">{{ t('key.price.calendar.date.tips.price') }}</span>
        </div>

        <!-- 票种选择 -->
        <template v-if="state.calendarData?.packages?.length !== 1">
          <div text="base black" font="600" m="t-5 b-3">
            {{ t('key.ticket.options') }}
          </div>
          <div flex="~ items-center wrap" p="b-3 r-4">
            <div
              v-for="item in state.calendarData?.packages" :key="item.packageId" class="relative rounded" max-w="full" flex="shrink-0" m="r-3 b-2"
              b="1.5 solid #D9D9D9" p="x-4 y-2.5" text="xs black"
              :style="[
                item.packageId === state.selected.packageId ? { borderColor: 'var(--tc-color-primary)', color: 'var(--tc-color-primary)', backgroundColor: 'var(--el-color-primary-light-9)' } : {},
                isDisabledTicketOption(item) ? { borderColor: '#D9D9D980', color: '#D9D9D9' } : {},
              ]"
              @click="onSelectPackage(item)"
            >
              {{ item.packageName }}
            </div>
          </div>
        </template>
      </div>

      <div p="t-3 b-4 x-4" b-t="1px solid #dddddd">
        <div flex="~ items-center justify-between" m="b-2">
          <div text="xl black" font="600">
            {{ getCurrencySymbol(userStore.userInfo.currency) }} {{ toThousands(mappedCalendarData?.[dayjs(state.selected.date).format('YYYY-MM-DD')]?.minPrice) }}
          </div>

          <VanTag v-if="state.cashbackDesc" color="var(--el-color-primary-light-9)" text-color="var(--tc-color-cashback-desc)">
            <span p="0.5">{{ state.cashbackDesc }}</span>
          </VanTag>
        </div>
        <VanButton
          type="primary"
          :block="true"
          :disabled="!state.selected.date || !state.selected.packageId" @click="onConfirm(attrId)"
        >
          <span text="base" font="600">{{ t('key.business.select.ticket.apply') }}</span>
        </VanButton>
      </div>
    </div>
  </div>

  <BaseAZCalendar
    v-model:show="showCalendar"
    class="az-price-calendar__popup"
    :default-selected-date="dayjs(state.selected.date).toDate()"
    :max-date="maxDate"
    :formatter="formatter"
    @confirm="onConfirmCalendar"
  >
    <template #bottom-info="data">
      <div class="relative">
        {{ mappedCalendarData?.[dayjs(data.date).format('YYYY-MM-DD')]?.minPrice || '-' }}
      </div>
    </template>
  </BaseAZCalendar>
</template>

<script lang="ts" setup>
import type { CalendarDayItem } from 'vant'
import type { TripProductPackagePriceCalendarDTO, TripProductPriceCalendarDTO } from '~/apis/back/types'
import { getCurrencySymbol, toThousands } from '~/utils'

defineProps({
  attrId: {
    type: Number,
  },
})

const { t } = useI18n()
const userStore = useUserStore()
const { state, getters: { mappedCalendarData, maxDate }, actions: { isDisabledDate, isDisabledTicketOption, onConfirm, fetchPriceCalendar } } = usePriceCalendarState()

await fetchPriceCalendar()

const dayjs = useDayjs()
const showCalendar = ref(false)

const defaultCalendarDate = computed(() => state.value.calendarData?.priceCalendars?.slice(0, 7))

function onSelectDate(item: TripProductPriceCalendarDTO) {
  if (isDisabledDate(item)) {
    return
  }

  state.value.selected.date = item.date || ''
}

function onSelectPackage(item: TripProductPackagePriceCalendarDTO) {
  if (isDisabledTicketOption(item)) {
    return
  }

  if (state.value.selected.packageId === item.packageId) {
    state.value.selected.packageId = ''
  }
  else {
    state.value.selected.packageId = item.packageId || ''
  }
}

function onConfirmCalendar(value: string) {
  state.value.selected.date = dayjs(value).format('YYYY-MM-DD')

  nextTick(() => {
    const element = document.querySelector(`[data-date="${state.value.selected.date}"]`)
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'center',
    })
  })
} // Adjust the import path as necessary

function formatter(day: CalendarDayItem) {
  const date = state.value.calendarData?.priceCalendars?.find(i => i.date === dayjs(day.date).format('YYYY-MM-DD'))

  if ((date && isDisabledDate(date)) || !date) {
    day.type = 'disabled'
  }
  return day
}
</script>

<style lang="scss">
.az-price-calendar {
  height: 100%;

  &__popup {
    .van-calendar__day, .van-calendar__selected-day {
      flex-direction: column;

      .van-calendar__bottom-info {
        position: relative;
        bottom: 2px;
        line-height: 1;
        color: #8592A6;
      }
    }

    .van-calendar__selected-day {
      .van-calendar__bottom-info {
        color: white;
      }
    }
  }
}
</style>
